import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { publicApi } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  FileText, 
  Clock, 
  CheckCircle2, 
  AlertCircle,
  ArrowRight,
  RefreshCw,
  Mail,
  Phone,
  Shield,
  User,
  FileCheck,
  Eye,
  Building2,
  Paperclip,
  Upload,
  Calendar,
  ChevronDown,
  LogOut
} from 'lucide-react';
import { format, formatDistanceToNow, isPast } from 'date-fns';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { Label } from '../components/ui/label';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Status configuration
const STATUS_CONFIG = {
  sent: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100', label: 'Not started', accent: 'border-l-blue-500' },
  opened: { icon: Eye, color: 'text-amber-600', bg: 'bg-amber-100', label: 'Opened', accent: 'border-l-amber-500' },
  in_progress: { icon: FileText, color: 'text-amber-600', bg: 'bg-amber-100', label: 'In progress', accent: 'border-l-amber-500' },
  completed: { icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Completed', accent: 'border-l-emerald-500' },
  pending_approval: { icon: FileCheck, color: 'text-purple-600', bg: 'bg-purple-100', label: 'Under review', accent: 'border-l-purple-500' },
  approved: { icon: Shield, color: 'text-emerald-600', bg: 'bg-emerald-100', label: 'Approved', accent: 'border-l-emerald-500' },
  expired: { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-100', label: 'Expired', accent: 'border-l-red-500' },
};

export default function CandidatePortalPage() {
  const [searchParams] = useSearchParams();
  const { token: portalToken, tenantSlug: urlTenantSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const legacyTokenRef = useRef(null); // Store legacy portal token for credential APIs
  const docsRef = useRef(null); // Ref to scroll to documents section
  const completedRef = useRef(null); // Ref to scroll to completed section
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [companySettings, setCompanySettings] = useState({
    website_url: '',
    company_name: '',
    company_logo_url: '',
    primary_color: '#f97316',
    secondary_color: '#f59e0b',
    tagline: '',
    about: '',
    welcome_message: '',
    support_email: '',
    support_phone: ''
  });
  // Click-to-filter — the Pending/In Progress/Complete tiles double as
  // toggles. Clicking a tile hides anything that doesn't match;
  // clicking it again clears the filter.
  const [statusFilter, setStatusFilter] = useState(null); // 'pending' | 'in_progress' | 'complete' | null

  // ── Upload state ──
  const [cvUploading, setCvUploading] = useState(false);
  const [currentCv, setCurrentCv] = useState(null); // single CV, not array
  const [cvError, setCvError] = useState('');
  const [cvSuccess, setCvSuccess] = useState('');
  const [credUploading, setCredUploading] = useState(false);
  const [availableCredentials, setAvailableCredentials] = useState([]);
  const [credentialsLoading, setCredentialsLoading] = useState(false);
  const [credPickerOpen, setCredPickerOpen] = useState(false);
  const [selectedCred, setSelectedCred] = useState(null);
  const [credExpiry, setCredExpiry] = useState('');
  const [credFile, setCredFile] = useState(null);
  const [credSearch, setCredSearch] = useState('');
  const [uploadedCredentials, setUploadedCredentials] = useState([]);
  const [credError, setCredError] = useState('');
  const [credSuccess, setCredSuccess] = useState('');
  const [cvBlobUrl, setCvBlobUrl] = useState(null);
  const [credPopoverOpen, setCredPopoverOpen] = useState(false);
  const [uiLabels, setUiLabels] = useState({ credentials: 'Credentials', documents: 'Documents', forms: 'Forms' });
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwLoading, setPwLoading] = useState(false);
  const [credsExpanded, setCredsExpanded] = useState(true);
  const cvInputRef = useRef(null);
  const credFileInputRef = useRef(null);

  const emailFromUrl = searchParams.get('email');
  const tenantIdFromUrl = searchParams.get('tenant_id');
  const [portalCandidate, setPortalCandidate] = useState(null);

  // Check portal auth first — redirect to login if not authenticated
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/portal/auth/me`, { credentials: 'include' });
        if (res.ok) {
          const candidate = await res.json();
          if (!cancelled) setPortalCandidate(candidate);
          // Fetch a legacy portal token so existing upload code keeps working
          try {
            const tokRes = await fetch(`${API_URL}/api/portal/auth/portal-token`, { credentials: 'include' });
            if (tokRes.ok) {
              const { portal_token } = await tokRes.json();
              if (!cancelled && portal_token) {
                // Store the legacy token for credential APIs
                legacyTokenRef.current = portal_token;
                window.__portalToken = portal_token;
              }
            }
          } catch { /* non-fatal */ }
        } else {
          if (!cancelled) {
            // Allow old token-based access as fallback (for /portal/access/:token)
            if (portalToken) return; // let old flow run
            const slug = window.location.pathname.split('/portal/')[1]?.split('/')[0];
            if (slug && slug !== 'access' && slug !== 'login' && slug !== 'reset-password') {
              navigate(`/portal/login/${slug}`);
            } else {
              navigate('/portal/login');
            }
          }
          return;
        }
      } catch {
        if (!cancelled && !portalToken) navigate('/portal/login');
        return;
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Once authenticated, load documents via the auth-scoped endpoint
  useEffect(() => {
    if (!portalCandidate) {
      // Fallback: if we have an old-style portalToken (from /portal/access/:token), use it
      if (portalToken) {
        loadDocumentsByToken(portalToken);
      }
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/portal/auth/documents`, { credentials: 'include' });
        if (!res.ok) throw new Error('Failed to load documents');
        const json = await res.json();
        // Map into the shape the portal page expects:
        // pending_documents = everything not yet completed
        // completed_documents = done
        const allDocs = json.documents || [];
        const pending = allDocs.filter(d => ['sent', 'opened', 'in_progress', 'pending_approval'].includes(d.status));
        const completed = allDocs.filter(d => ['completed', 'approved'].includes(d.status));
        const expired = allDocs.filter(d => d.status === 'expired');
        setData({
          outstanding_documents: pending,
          pending_documents: pending,
          completed_documents: completed,
          expired_documents: expired,
          pending_forms: json.pending_forms || [],
          completed_forms: json.completed_forms || [],
          pending_credentials: json.pending_credentials || [],
          completed_credentials: json.completed_credentials || [],
          candidate_name: portalCandidate.name || json.candidate?.name || '',
          candidate_email: portalCandidate.email,
          email: portalCandidate.email,
          name: portalCandidate.name,
          tenant_id: portalCandidate.tenant_id,
        });
      } catch (err) {
        setError('Failed to load your documents. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [portalCandidate]);

  // Load company branding + UI labels once we know the tenant
  useEffect(() => {
    const tid = portalCandidate?.tenant_id;
    if (!tid) return;
    loadCompanySettings(tid);
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/settings/ui-labels?tenant_id=${encodeURIComponent(tid)}`);
        if (res.ok) {
          const json = await res.json();
          setUiLabels(prev => ({ ...prev, ...json }));
        }
      } catch { /* silent */ }
    })();
  }, [portalCandidate?.tenant_id]);

  // Re-fetch candidate info + documents + uploads when tab regains focus
  // (e.g. after completing a document in another tab or uploading a credential)
  useEffect(() => {
    const refresh = async () => {
      if (document.visibilityState !== 'visible' || !portalCandidate) return;
      try {
        const [meRes, docsRes, uploadsRes] = await Promise.allSettled([
          fetch(`${API_URL}/api/portal/auth/me`, { credentials: 'include' }),
          fetch(`${API_URL}/api/portal/auth/documents`, { credentials: 'include' }),
          fetch(`${API_URL}/api/portal/auth/uploads`, { credentials: 'include' }),
        ]);
        if (meRes.status === 'fulfilled' && meRes.value.ok) {
          const me = await meRes.value.json();
          setPortalCandidate(prev => ({ ...prev, ...me }));
        }
        if (docsRes.status === 'fulfilled' && docsRes.value.ok) {
          const json = await docsRes.value.json();
          const allDocs = json.documents || [];
          const pending = allDocs.filter(d => ['sent', 'opened', 'in_progress', 'pending_approval'].includes(d.status));
          const completed = allDocs.filter(d => ['completed', 'approved'].includes(d.status));
          const expired = allDocs.filter(d => d.status === 'expired');
          setData(prev => ({
            ...prev,
            outstanding_documents: pending,
            pending_documents: pending,
            completed_documents: completed,
            expired_documents: expired,
            pending_forms: json.pending_forms || prev?.pending_forms || [],
            completed_forms: json.completed_forms || prev?.completed_forms || [],
            pending_credentials: json.pending_credentials || prev?.pending_credentials || [],
            completed_credentials: json.completed_credentials || prev?.completed_credentials || [],
            candidate_name: json.candidate?.name || prev?.candidate_name,
          }));
        }
        // Refresh self-uploaded credentials (My Qualifications) so they
        // stay visible after the candidate uploads one and then switches tabs
        if (uploadsRes.status === 'fulfilled' && uploadsRes.value.ok) {
          const json = await uploadsRes.value.json();
          const uploads = json.uploads || [];
          const latestCv = uploads.find(u => u.type === 'cv' && u.storage_key);
          if (latestCv) {
            setCurrentCv({
              id: latestCv.id,
              filename: latestCv.filename,
              size: latestCv.file_size,
              contentType: latestCv.content_type,
              uploadedAt: latestCv.uploaded_at,
              previewUrl: `${API_URL}/api/portal/file/${latestCv.id}`,
            });
          }
          const creds = uploads.filter(u => u.type === 'credential' && u.storage_key);
          setUploadedCredentials(creds.map(c => ({
            id: c.id,
            name: c.credential_name,
            expiry: c.expiry_date,
            filename: c.filename,
            uploadedAt: c.uploaded_at,
            previewUrl: `${API_URL}/api/portal/file/${c.id}`,
          })));
        }
      } catch { /* silent */ }
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, [portalCandidate]);

  // Fetch CV as blob for reliable inline preview (bypasses proxy issues)
  useEffect(() => {
    if (!currentCv?.previewUrl) {
      if (cvBlobUrl) { URL.revokeObjectURL(cvBlobUrl); setCvBlobUrl(null); }
      return;
    }
    const isPdf = currentCv.contentType === 'application/pdf';
    const isImage = currentCv.contentType?.startsWith('image/');
    if (!isPdf && !isImage) { setCvBlobUrl(null); return; }

    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(currentCv.previewUrl);
        if (!res.ok || cancelled) return;
        const blob = await res.blob();
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        setCvBlobUrl(url);
      } catch (_) { /* silent — "Open in new tab" link still works */ }
    })();
    return () => {
      cancelled = true;
      if (cvBlobUrl) URL.revokeObjectURL(cvBlobUrl);
    };
  }, [currentCv?.previewUrl, currentCv?.contentType]);

  // Re-hydrate the uploaded-CV list from the backend so it survives a refresh
  // (it used to live only in React state with a session-only blob URL).
  // Load persisted uploads (CV + credentials) from DB.
  // IMPORTANT: Only runs on initial mount (empty dep array) — the visibility
  // refresh handler above keeps the data fresh on tab-switch / focus events.
  // Using portalCandidate as dependency caused re-runs on every focus event
  // because the visibility handler updates portalCandidate. This was a source
  // of credentials "disappearing" when the effect re-triggered and the async
  // race lost against the cleanup function.
  const uploadsLoadedRef = useRef(false);
  useEffect(() => {
    if (!portalCandidate || uploadsLoadedRef.current) return;
    uploadsLoadedRef.current = true;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/portal/auth/uploads`, { credentials: 'include' });
        if (!res.ok || cancelled) return;
        const json = await res.json();
        const uploads = json.uploads || [];
        // Restore CV
        const latestCv = uploads.find(u => u.type === 'cv' && u.storage_key);
        if (latestCv && !cancelled) {
          setCurrentCv({
            id: latestCv.id,
            filename: latestCv.filename,
            size: latestCv.file_size,
            contentType: latestCv.content_type,
            uploadedAt: latestCv.uploaded_at,
            previewUrl: `${API_URL}/api/portal/file/${latestCv.id}`,
          });
        }
        // Restore credentials — always set state to reflect DB truth,
        // even when empty, so stale in-session data doesn't persist.
        const creds = uploads.filter(u => u.type === 'credential' && u.storage_key);
        if (!cancelled) {
          setUploadedCredentials(creds.map(c => ({
            id: c.id,
            name: c.credential_name,
            expiry: c.expiry_date,
            filename: c.filename,
            uploadedAt: c.uploaded_at,
            previewUrl: `${API_URL}/api/portal/file/${c.id}`,
          })));
        }
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [portalCandidate]);

  const loadCompanySettings = async (tenantId) => {
    try {
      const url = tenantId
        ? `${API_URL}/api/settings/company?tenant_id=${encodeURIComponent(tenantId)}`
        : `${API_URL}/api/settings/company`;
      const response = await fetch(url);
      if (response.ok) {
        const settings = await response.json();
        setCompanySettings(prev => ({ ...prev, ...settings }));
      }
    } catch (error) {
      // Silent fail — defaults render fine; toast would be noisy on a public page
    }
  };

  const loadDocuments = async (email, { background = false } = {}) => {
    if (!background) { setLoading(true); setError(null); }
    try {
      const response = await publicApi.getDocumentsByEmail(email, tenantIdFromUrl);
      setData(response.data);
    } catch (error) {
      if (background) return; // silent background refresh — keep showing current data
      setError('Unable to load your documents. Please check your link or contact support.');
    } finally {
      if (!background) setLoading(false);
    }
  };

  const loadDocumentsByToken = async (tok, { background = false } = {}) => {
    if (!background) { setLoading(true); setError(null); }
    try {
      const response = await publicApi.getDocumentsByPortalToken(tok);
      setData(response.data);
    } catch (err) {
      if (background) return; // silent background refresh — keep showing current data
      const status = err?.response?.status;
      if (status === 401) {
        setError('This link has expired. Please contact your recruiter for a new one.');
      } else {
        setError('Unable to load your documents. Please contact support.');
      }
    } finally {
      if (!background) setLoading(false);
    }
  };

  const handleStartDocument = (doc) => {
    navigate(`/complete/${doc.completion_token}`);
  };

  // ── CV Upload handler (single CV, replace on re-upload) ──
  const handleCvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const effectiveToken = portalToken || legacyTokenRef.current || window.__portalToken;
    if (!effectiveToken) { setCvError('Portal session expired. Please refresh the page.'); return; }
    setCvUploading(true);
    setCvError('');
    setCvSuccess('');
    try {
      const fd = new FormData();
      fd.append('portal_token', effectiveToken);
      fd.append('file', file);
      const res = await fetch(`${API_URL}/api/portal/upload-cv`, { method: 'POST', body: fd });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || `Upload failed (${res.status})`);
      setCurrentCv({
        id: json.upload_id,
        filename: json.filename,
        size: json.file_size,
        contentType: json.content_type,
        uploadedAt: new Date().toISOString(),
        previewUrl: `${API_URL}${json.preview_url}`,
      });
      setCvSuccess(`"${file.name}" uploaded successfully`);
    } catch (err) {
      setCvError(err.message || 'Failed to upload CV. Please try again.');
    } finally {
      setCvUploading(false);
      e.target.value = '';
    }
  };

  // ── Remove an uploaded CV (deletes it from Bullhorn too) ──
  // ── Credential upload handlers ──
  const loadAvailableCredentials = async () => {
    if (availableCredentials.length > 0 || credentialsLoading) return;
    setCredentialsLoading(true);
    try {
      // Use cookie-authenticated endpoint when available (new auth flow)
      let res;
      if (portalCandidate) {
        res = await fetch(`${API_URL}/api/portal/auth/available-credentials`, { credentials: 'include' });
      } else {
        // Fallback to legacy token-based endpoint
        const effectiveToken = portalToken || legacyTokenRef.current || window.__portalToken;
        if (!effectiveToken) { setCredentialsLoading(false); return; }
        res = await fetch(`${API_URL}/api/portal/available-credentials?portal_token=${encodeURIComponent(effectiveToken)}`);
      }
      if (!res.ok) throw new Error('Failed to load');
      const json = await res.json();
      setAvailableCredentials(json.credentials || []);
    } catch (err) {
      console.error('Failed to load credentials:', err);
      toast.error('Failed to load credentials list');
    } finally {
      setCredentialsLoading(false);
    }
  };

  const handleCredentialUpload = async () => {
    const effectiveToken = portalToken || legacyTokenRef.current || window.__portalToken;
    const expiryRequired = selectedCred && !selectedCred.expiry_optional;
    if (!selectedCred || (expiryRequired && !credExpiry) || !credFile || !effectiveToken) {
      setCredError(expiryRequired
        ? 'Please select a credential, set an expiry date, and choose a file.'
        : 'Please select a credential and choose a file.');
      return;
    }
    setCredUploading(true);
    setCredError('');
    setCredSuccess('');
    try {
      const fd = new FormData();
      fd.append('portal_token', effectiveToken);
      fd.append('credential_id', selectedCred.id);
      fd.append('credential_name', selectedCred.name);
      if (credExpiry) fd.append('expiry_date', credExpiry);
      fd.append('file', credFile);
      const res = await fetch(`${API_URL}/api/portal/upload-credential`, { method: 'POST', body: fd });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || `Upload failed (${res.status})`);
      setCredSuccess(`"${selectedCred.name}" uploaded successfully`);
      setUploadedCredentials(prev => [...prev, {
        id: json.upload_id,
        name: selectedCred.name,
        expiry: credExpiry || 'No expiry',
        filename: json.filename,
        uploadedAt: new Date().toISOString(),
        previewUrl: `${API_URL}${json.preview_url}`,
      }]);
      setSelectedCred(null);
      setCredExpiry('');
      setCredFile(null);
      setCredPickerOpen(false);
    } catch (err) {
      setCredError(err.message || 'Failed to upload credential. Please try again.');
    } finally {
      setCredUploading(false);
    }
  };

  // Dynamic styles based on company branding
  const brandGradient = `linear-gradient(135deg, ${companySettings.primary_color} 0%, ${companySettings.secondary_color} 100%)`;
  function isLightColor(hex) {
    if (!hex || hex.length < 7) return false;
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return (r * 299 + g * 587 + b * 114) / 1000 > 180;
  }
  const headerTextColor = isLightColor(companySettings.primary_color) ? '#1a1a1a' : '#ffffff';

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-200/70">
        <div className="text-center text-slate-600">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4" />
          <p>Loading your documents...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-200/70">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-bold mb-2">Something went wrong</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            {companySettings.support_email && (
              <p className="text-sm text-muted-foreground">
                Contact: <a href={`mailto:${companySettings.support_email}`} className="text-primary underline">{companySettings.support_email}</a>
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const pendingCount = data?.pending_documents?.length || 0;
  const completedCount = data?.completed_documents?.length || 0;
  const pendingCredentialCount = data?.pending_credentials?.length || 0;
  const completedCredentialCount = data?.completed_credentials?.length || 0;
  const pendingFormCount = data?.pending_forms?.length || 0;
  const completedFormCount = data?.completed_forms?.length || 0;
  const totalCount = pendingCount + completedCount + pendingCredentialCount + completedCredentialCount + pendingFormCount + completedFormCount;
  const totalPending = pendingCount + pendingCredentialCount + pendingFormCount;
  const totalCompleted = completedCount + completedCredentialCount + completedFormCount;
  const inProgressCount = (data?.pending_documents?.filter(d => ['opened', 'in_progress'].includes(d.status)).length || 0)
    + (data?.pending_credentials?.filter(c => c.status === 'in_progress').length || 0)
    + (data?.pending_forms?.filter(f => f.status === 'in_progress').length || 0);

  // Filter helpers — when a status tile is selected we hide everything
  // that doesn't match. `null` = no filter (show everything).
  const matchesFilter = (it, kind) => {
    if (!statusFilter) return true;
    if (kind === 'document') {
      if (statusFilter === 'pending') return !['opened', 'in_progress'].includes(it.status);
      if (statusFilter === 'in_progress') return ['opened', 'in_progress'].includes(it.status);
      return false;
    }
    if (kind === 'credential' || kind === 'form') {
      if (statusFilter === 'pending') return it.status !== 'in_progress';
      if (statusFilter === 'in_progress') return it.status === 'in_progress';
      return false;
    }
    return true;
  };
  const filteredPendingDocs = (data?.pending_documents || []).filter(d => matchesFilter(d, 'document'));
  const tsSort = (a, b) => {
    const ta = new Date(a.sent_at || a.created_at || 0).getTime();
    const tb = new Date(b.sent_at || b.created_at || 0).getTime();
    return tb - ta; // newest first
  };
  const filteredPendingCreds = (data?.pending_credentials || []).filter(c => matchesFilter(c, 'credential')).sort(tsSort);
  const filteredPendingForms = (data?.pending_forms || []).filter(f => matchesFilter(f, 'form')).sort(tsSort);
  const showCompleted = !statusFilter || statusFilter === 'complete';

  // Group pending documents by their parent packet, sort each packet's
  // docs *newest-first* (by `sent_at` desc, falling back to
  // `created_at`), and sort packet groups by the most-recent send
  // within them so the candidate's brand-new docs appear at the top.
  // Docs without a packet land under a `__solo__` bucket which also
  // renders newest-first; that bucket sorts into the packet group
  // ordering using the same most-recent-send tie-breaker.
  const groupedPendingDocs = (() => {
    const buckets = new Map();
    for (const d of filteredPendingDocs) {
      const key = d.packet_id || '__solo__';
      const name = d.packet_name || (key === '__solo__' ? 'Documents' : 'Pack');
      if (!buckets.has(key)) buckets.set(key, { key, name, docs: [] });
      buckets.get(key).docs.push(d);
    }
    const tsOf = (d) => {
      const raw = d.sent_at || d.created_at || d.updated_at;
      const t = raw ? new Date(raw).getTime() : 0;
      return Number.isFinite(t) ? t : 0;
    };
    // Sort docs newest-first within each bucket and compute the
    // bucket's "freshness" = the max sent_at of any doc in it.
    const all = Array.from(buckets.values()).map((g) => {
      g.docs.sort((a, b) => tsOf(b) - tsOf(a));
      g.latest = g.docs.length ? tsOf(g.docs[0]) : 0;
      return g;
    });
    // Sort all groups by freshness — newest first, including solo docs
    return all.sort((a, b) => b.latest - a.latest);
  })();

  return (
    <div className="min-h-screen flex flex-col bg-slate-200/70">
      {/* Brand colour bar */}
      <div className="h-2 flex shrink-0">
        <div className="flex-1" style={{ backgroundColor: companySettings.primary_color }} />
        <div className="flex-1" style={{ backgroundColor: companySettings.secondary_color }} />
      </div>

      {/* Company header — clean white card */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-5 sm:py-6">
          <div className="flex items-center gap-4 flex-wrap justify-center sm:justify-start">
            {companySettings.company_logo_url ? (
              <img
                src={`${API_URL}${companySettings.company_logo_url}`}
                alt={companySettings.company_name || 'Company logo'}
                className="h-12 sm:h-16 max-w-[180px] object-contain"
                data-testid="portal-company-logo"
              />
            ) : (
              <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl flex items-center justify-center shrink-0"
                   style={{ backgroundColor: companySettings.primary_color }}>
                <Building2 className="w-6 h-6 sm:w-8 sm:h-8" style={{ color: headerTextColor }} />
              </div>
            )}
            <div className="text-center sm:text-left">
              <h1 className="text-lg sm:text-xl font-bold text-slate-800" data-testid="portal-company-name">
                {companySettings.company_name || 'Document Portal'}
              </h1>
              {companySettings.tagline && (
                <p className="text-xs sm:text-sm text-slate-500 mt-0.5" data-testid="portal-tagline">
                  {companySettings.tagline}
                </p>
              )}
            </div>
          </div>
          {companySettings.about && (
            <p className="text-xs sm:text-sm text-slate-500 mt-3 max-w-2xl whitespace-pre-line" data-testid="portal-about">
              {companySettings.about}
            </p>
          )}
        </div>
      </header>

      {/* Welcome message — below the header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-3">
          <p className="text-sm text-left text-muted-foreground whitespace-pre-line" data-testid="portal-welcome">
            {companySettings.welcome_message || 'Complete your onboarding documents below.'}
          </p>
        </div>
      </div>

      {/* User info bar */}
      <div className="bg-white border-b px-4 py-2.5 shadow-sm">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <User className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium truncate max-w-[150px] sm:max-w-none">{portalCandidate?.name || data?.candidate_name || data?.name || 'Candidate'}</span>
            <span className="hidden sm:inline text-xs text-muted-foreground">({portalCandidate?.email || data?.candidate_email || data?.email})</span>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowChangePassword(true)}
              className="text-xs text-slate-500 hover:text-slate-700 transition hover:underline"
              data-testid="portal-change-password-link"
            >
              Change Password
            </button>
            <button
              onClick={async () => {
                try {
                  await fetch(`${API_URL}/api/portal/auth/logout`, { method: 'POST', credentials: 'include' });
                } catch { /* logout best-effort */ }
                const slug = urlTenantSlug || window.location.pathname.split('/portal/')[1]?.split('/')[0];
                navigate(slug && slug !== 'access' ? `/portal/login/${slug}` : '/portal/login');
              }}
              className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 transition px-2 py-1 rounded hover:bg-slate-100"
              data-testid="portal-logout-btn"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span>Log out</span>
            </button>
          </div>
        </div>
      </div>

      {/* Change Password Modal */}
      {showChangePassword && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowChangePassword(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6" onClick={e => e.stopPropagation()} data-testid="change-password-modal">
            <h3 className="font-semibold text-lg text-slate-800 mb-4">Change Password</h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              if (pwForm.newPw.length < 8) { toast.error('New password must be at least 8 characters'); return; }
              if (pwForm.newPw !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
              setPwLoading(true);
              try {
                const res = await fetch(`${API_URL}/api/portal/auth/change-password`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  credentials: 'include',
                  body: JSON.stringify({ current_password: pwForm.current, new_password: pwForm.newPw }),
                });
                const data = await res.json();
                if (res.ok) {
                  toast.success('Password changed successfully');
                  setShowChangePassword(false);
                  setPwForm({ current: '', newPw: '', confirm: '' });
                } else {
                  toast.error(data.detail || 'Failed to change password');
                }
              } catch { toast.error('Failed to change password'); }
              finally { setPwLoading(false); }
            }} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Current Password</label>
                <input
                  type="password"
                  value={pwForm.current}
                  onChange={e => setPwForm(p => ({ ...p, current: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  required
                  data-testid="change-pw-current"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">New Password</label>
                <input
                  type="password"
                  value={pwForm.newPw}
                  onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  placeholder="Minimum 8 characters"
                  minLength={8}
                  required
                  data-testid="change-pw-new"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={pwForm.confirm}
                  onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                  required
                  data-testid="change-pw-confirm"
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowChangePassword(false)}
                  className="flex-1 py-2 text-sm text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={pwLoading}
                  className="flex-1 py-2 text-sm text-white bg-slate-800 rounded-lg hover:bg-slate-700 transition disabled:opacity-50"
                  data-testid="change-pw-submit"
                >
                  {pwLoading ? 'Saving...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Main content */}
      <main className="flex-1 px-4 py-6">
        <div className="max-w-4xl mx-auto space-y-4">
          
          {/* Progress summary — sticky on scroll */}
          <div className="sticky top-0 z-20 bg-slate-200/70 -mx-4 px-4 py-2">
            <div className="max-w-4xl mx-auto bg-white rounded-2xl p-4 sm:p-5 shadow-sm border">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold">Your Progress</h2>
              <span className="text-sm text-muted-foreground">
                {totalCompleted} of {totalCount} completed
              </span>
            </div>

            {/* Progress bar */}
            <div className="h-3 bg-slate-100 rounded-full overflow-hidden mb-4">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${totalCount > 0 ? (totalCompleted / totalCount) * 100 : 0}%`,
                  background: brandGradient
                }}
              />
            </div>

            {/* Clickable status filters — clicking a tile filters the
                lists below to just that bucket; clicking the active
                tile again clears the filter. */}
            <div className="grid grid-cols-3 gap-2 text-center">
              {[
                { key: 'pending', label: 'Pending', count: totalPending - inProgressCount, bg: 'bg-blue-50', activeBg: 'bg-blue-100', ring: 'ring-blue-400', text: 'text-blue-600' },
                { key: 'in_progress', label: 'In Progress', count: inProgressCount, bg: 'bg-amber-50', activeBg: 'bg-amber-100', ring: 'ring-amber-400', text: 'text-amber-600' },
                { key: 'complete', label: 'Complete', count: totalCompleted, bg: 'bg-emerald-50', activeBg: 'bg-emerald-100', ring: 'ring-emerald-400', text: 'text-emerald-600' },
              ].map((tile) => {
                const active = statusFilter === tile.key;
                return (
                  <button
                    key={tile.key}
                    type="button"
                    onClick={() => {
                      const newFilter = active ? null : tile.key;
                      setStatusFilter(newFilter);
                      // Scroll to the relevant section
                      if (newFilter) {
                        const targetRef = newFilter === 'complete' ? completedRef : docsRef;
                        setTimeout(() => targetRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
                      }
                    }}
                    className={`p-2 rounded-lg transition-all ${active ? `${tile.activeBg} ring-2 ${tile.ring}` : `${tile.bg} hover:brightness-95`} cursor-pointer`}
                    data-testid={`progress-tile-${tile.key}`}
                  >
                    <p className={`text-lg sm:text-2xl font-bold ${tile.text}`}>{tile.count}</p>
                    <p className={`text-xs ${tile.text}/70`}>{tile.label}</p>
                  </button>
                );
              })}
            </div>
            {statusFilter && (
              <div className="mt-3 text-center">
                <button
                  type="button"
                  onClick={() => setStatusFilter(null)}
                  className="text-xs text-slate-500 hover:text-slate-700 underline"
                  data-testid="clear-status-filter"
                >
                  Clear filter
                </button>
              </div>
            )}
          </div>
          </div>

          {/* ── CV Upload Section (single CV with replace) ── */}
          {portalCandidate && (
            <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border" data-testid="cv-upload-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-violet-100 flex items-center justify-center">
                    <Upload className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">Upload your CV</h3>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <input
                    ref={cvInputRef}
                    type="file"
                    style={{ position: 'absolute', width: 1, height: 1, opacity: 0, overflow: 'hidden', pointerEvents: 'none' }}
                    accept=".pdf,.doc,.docx,.rtf,.txt"
                    onChange={handleCvUpload}
                    disabled={cvUploading}
                    data-testid="cv-upload-input"
                  />
                  <Button
                    onClick={() => cvInputRef.current?.click()}
                    disabled={cvUploading}
                    className={cvUploading ? 'bg-slate-400' : 'bg-violet-600 hover:bg-violet-700'}
                    data-testid="cv-upload-btn"
                  >
                    {cvUploading ? (
                      <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Uploading...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> {currentCv ? 'Replace CV' : 'Choose File'}</>
                    )}
                  </Button>
                </div>
              </div>
              {cvError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2" data-testid="cv-error">
                  <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-red-700">{cvError}</p>
                </div>
              )}
              {cvSuccess && (
                <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-start gap-2" data-testid="cv-success">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-emerald-700">{cvSuccess}</p>
                </div>
              )}
              {/* Current CV display with inline preview */}
              {currentCv && (
                <div className="mt-3 border-t pt-3">
                  <div className="flex items-center gap-3 p-3 bg-violet-50 rounded-lg">
                    <FileText className="w-5 h-5 text-violet-600 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{currentCv.filename}</p>
                      <p className="text-xs text-muted-foreground">
                        {currentCv.size ? `${(currentCv.size / 1024).toFixed(0)} KB · ` : ''}
                        Uploaded {format(new Date(currentCv.uploadedAt), 'dd MMM yyyy, HH:mm')}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <a
                        href={currentCv.previewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-violet-600 hover:underline font-medium"
                        data-testid="cv-open-tab-link"
                      >
                        Open in new tab ↗
                      </a>
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── My Qualifications (self-upload) — right under CV ── */}
          {portalCandidate && (
            <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border" data-testid="my-qualifications-section">
              <button
                onClick={() => setCredsExpanded(prev => !prev)}
                className="flex items-center justify-between w-full text-left"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Upload className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-sm">My {uiLabels.credentials || 'Qualifications'}</h3>
                    <p className="text-xs text-muted-foreground">Upload your own {(uiLabels.credentials || 'qualifications').toLowerCase()}</p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${credsExpanded ? '' : '-rotate-90'}`} />
              </button>

              {credsExpanded && (
                <div className="mt-4 space-y-3">
                  {uploadedCredentials.length > 0 && (
                    <div className="space-y-2">
                      {uploadedCredentials.map((uc, idx) => (
                        <div key={uc.id || idx} className="flex items-center gap-2 p-2.5 bg-emerald-50 rounded-lg text-sm">
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium truncate">{uc.name}</p>
                            <p className="text-xs text-muted-foreground">Expires: {uc.expiry} {uc.filename ? `· ${uc.filename}` : ''}</p>
                          </div>
                          {uc.previewUrl && (
                            <a href={uc.previewUrl} target="_blank" rel="noopener noreferrer" className="text-xs text-emerald-600 hover:underline font-medium flex-shrink-0">View</a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="border border-dashed border-emerald-300 rounded-lg p-4 space-y-3">
                    <Popover open={credPopoverOpen} onOpenChange={(open) => {
                      setCredPopoverOpen(open);
                      if (open) loadAvailableCredentials();
                    }}>
                      <PopoverTrigger asChild>
                        <button type="button" className="w-full text-left px-3 py-2.5 border rounded-lg text-sm bg-white hover:bg-slate-50 flex items-center justify-between" data-testid="my-qual-picker-btn">
                          <span className={selectedCred ? 'text-slate-900' : 'text-slate-400'}>
                            {selectedCred ? selectedCred.name : `Select ${(uiLabels.credentials || 'qualification').toLowerCase()} type...`}
                          </span>
                          <ChevronDown className="w-4 h-4 text-slate-400" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="p-0 w-72 z-50" align="start">
                        <div className="p-2 border-b">
                          <input type="text" className="w-full px-3 py-1.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-400"
                            placeholder={`Search ${(uiLabels.credentials || 'qualifications').toLowerCase()}...`}
                            value={credSearch} onChange={(e) => setCredSearch(e.target.value)} autoFocus />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {credentialsLoading ? (
                            <div className="p-4 text-sm text-center text-muted-foreground"><RefreshCw className="w-4 h-4 animate-spin inline mr-2" />Loading...</div>
                          ) : availableCredentials.filter(c => !credSearch || c.name.toLowerCase().includes(credSearch.toLowerCase())).length === 0 ? (
                            <div className="p-3 text-sm text-muted-foreground text-center">No matches</div>
                          ) : (
                            availableCredentials.filter(c => !credSearch || c.name.toLowerCase().includes(credSearch.toLowerCase())).map(c => (
                              <button key={c.id} type="button"
                                className={`w-full text-left px-3 py-2 text-sm hover:bg-muted/50 ${selectedCred?.id === c.id ? 'bg-emerald-50 font-medium' : ''}`}
                                onClick={() => { setSelectedCred(c); setCredPopoverOpen(false); }}>
                                {c.name}
                              </button>
                            ))
                          )}
                        </div>
                      </PopoverContent>
                    </Popover>

                    {selectedCred && (
                      <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs font-medium text-slate-600 mb-1 block">
                              Expiry Date
                              {selectedCred.expiry_optional && (
                                <span className="text-slate-400 font-normal ml-1">(optional)</span>
                              )}
                            </label>
                            <input
                              type="date"
                              className="w-full border rounded-lg px-3 py-2 text-sm"
                              value={credExpiry}
                              onChange={(e) => setCredExpiry(e.target.value)}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-medium text-slate-600 mb-1 block">File</label>
                            <input type="file" className="w-full text-xs file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200 max-w-full overflow-hidden"
                              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => setCredFile(e.target.files?.[0] || null)} data-testid="my-qual-file" />
                          </div>
                        </div>
                        <Button size="sm" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={(!selectedCred.expiry_optional && !credExpiry) || !credFile || credUploading} onClick={handleCredentialUpload}>
                          {credUploading ? <><RefreshCw className="w-4 h-4 animate-spin mr-2" /> Uploading...</> : <><Upload className="w-4 h-4 mr-2" /> Upload {uiLabels.credentials?.replace(/s$/i, '') || 'Qualification'}</>}
                        </Button>
                        {credError && <p className="text-xs text-red-600">{credError}</p>}
                        {credSuccess && <p className="text-xs text-emerald-600">{credSuccess}</p>}
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Documents & Forms (full width) ── */}
          {(filteredPendingDocs.length > 0 || filteredPendingCreds.length > 0 || filteredPendingForms.length > 0 || portalCandidate) && statusFilter !== 'complete' && (
          <div ref={docsRef} className="space-y-4" data-testid="documents-section">
            {/* Documents (grouped by parent packet) */}
            {filteredPendingDocs.length > 0 && (
              <div data-testid="pending-documents-section">
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-3 px-1">
                  Documents ({filteredPendingDocs.length})
                </h3>
                <div className="space-y-5">
                  {groupedPendingDocs.map((group) => (
                    <div key={group.key} data-testid={`packet-group-${group.key}`}>
                      {group.key !== '__solo__' && (
                        <div className="flex items-center gap-2 mb-2 px-1">
                          <span className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            Pack:
                          </span>
                          <span className="text-xs font-semibold text-slate-700 truncate">
                            {group.name}
                          </span>
                          <span className="text-[11px] text-slate-400">
                            ({group.docs.length})
                          </span>
                        </div>
                      )}
                      <div className="space-y-3">
                        {group.docs.map((doc) => {
                          const status = STATUS_CONFIG[doc.status] || STATUS_CONFIG.sent;
                          const StatusIcon = status.icon;
                          const isExpired = doc.expires_at && isPast(new Date(doc.expires_at));
                          const timeLeft = doc.expires_at
                            ? formatDistanceToNow(new Date(doc.expires_at), { addSuffix: true })
                            : 'No expiry';
                          if (isExpired) return null;
                          return (
                            <Card
                              key={doc.id}
                              className={`overflow-hidden border-l-4 ${status.accent} hover:shadow-md transition-shadow cursor-pointer`}
                              onClick={() => handleStartDocument(doc)}
                              data-testid={`pending-doc-${doc.id}`}
                            >
                              <CardContent className="p-4">
                                <div className="flex items-start gap-3">
                                  <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl ${status.bg} flex items-center justify-center flex-shrink-0`}>
                                    <StatusIcon className={`w-5 h-5 sm:w-6 sm:h-6 ${status.color}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <h4 className="font-semibold text-sm sm:text-base break-words" data-testid={`pending-doc-name-${doc.id}`}>{doc.template_name}</h4>
                                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-muted-foreground mt-1">
                                      <span className={`font-medium ${status.color}`}>{status.label}</span>
                                      {doc.sent_at && (
                                        <>
                                          <span className="text-slate-300">·</span>
                                          <span>Sent {format(new Date(doc.sent_at), 'dd MMM yyyy, HH:mm')}</span>
                                        </>
                                      )}
                                      <span className="text-slate-300">·</span>
                                      <span className="flex items-center gap-1">
                                        <Clock className="w-3 h-3" />
                                        Expires {timeLeft}
                                      </span>
                                      {doc.template_mode === 'attachment_only' && (
                                        <>
                                          <span className="text-slate-300">·</span>
                                          <span className="inline-flex items-center gap-1 text-amber-700 font-medium">
                                            <Paperclip className="w-3 h-3" /> Upload only
                                          </span>
                                        </>
                                      )}
                                    </div>
                                    {doc.candidate_instructions && (
                                      <p className="text-xs text-slate-600 mt-1.5 whitespace-pre-wrap break-words">
                                        {String(doc.candidate_instructions).replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()}
                                      </p>
                                    )}
                                  </div>
                                  <Button
                                    size="sm"
                                    className="flex-shrink-0 h-8 sm:h-9"
                                    style={{ background: brandGradient }}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStartDocument(doc);
                                    }}
                                  >
                                    <span className="hidden sm:inline mr-1">
                                      {doc.status === 'sent' ? 'Start' : 'Continue'}
                                    </span>
                                    <ArrowRight className="w-4 h-4" />
                                  </Button>
                                </div>
                              </CardContent>
                            </Card>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Requested Credentials column ── */}
            {filteredPendingCreds.length > 0 && (
            <div data-testid="requested-credentials-section">
              <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-3 px-1">
                Requested {uiLabels.credentials} ({filteredPendingCreds.length})
              </h3>
              <div className="space-y-3">
                {filteredPendingCreds.map((cr) => (
                  <Card
                    key={cr.id}
                    className="overflow-hidden border-l-4 border-l-sky-500 hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => navigate(cr.completion_path)}
                    data-testid={`pending-cred-${cr.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-sky-100 flex items-center justify-center flex-shrink-0">
                          <Paperclip className="w-5 h-5 sm:w-6 sm:h-6 text-sky-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-semibold text-sm sm:text-base break-words">{cr.name}</h4>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-muted-foreground mt-1">
                            <span className="font-medium text-sky-700">
                              {cr.items_completed} of {cr.items_total} uploaded
                            </span>
                            {cr.sent_at && (
                              <>
                                <span className="text-slate-300">·</span>
                                <span>Sent {format(new Date(cr.sent_at), 'dd MMM yyyy, HH:mm')}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          className="flex-shrink-0 h-8 sm:h-9 bg-sky-600 hover:bg-sky-700"
                          onClick={(e) => { e.stopPropagation(); navigate(cr.completion_path); }}
                        >
                          <span className="hidden sm:inline mr-1">{cr.items_completed > 0 ? 'Continue' : 'Start'}</span>
                          <ArrowRight className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            )}


            {/* ── Forms ── */}
            {filteredPendingForms.length > 0 && (
              <div data-testid="pending-forms-section">
                <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-3 px-1">
                  Forms ({filteredPendingForms.length})
                </h3>
                <div className="space-y-3">
                  {filteredPendingForms.map((fm) => {
                    const isInProgress = fm.status === 'in_progress';
                    return (
                      <Card
                        key={fm.id}
                        className="overflow-hidden border-l-4 border-l-violet-500 hover:shadow-md transition-shadow cursor-pointer"
                        onClick={() => fm.completion_path && navigate(fm.completion_path)}
                        data-testid={`pending-form-${fm.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-violet-100 flex items-center justify-center flex-shrink-0">
                              <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-violet-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-sm sm:text-base break-words">{fm.name}</h4>
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs sm:text-sm text-muted-foreground mt-1">
                                <span className="font-medium text-violet-700">
                                  {isInProgress ? 'In progress' : 'Not started'}
                                </span>
                                {fm.sent_at && (
                                  <>
                                    <span className="text-slate-300">·</span>
                                    <span>Sent {format(new Date(fm.sent_at), 'dd MMM yyyy, HH:mm')}</span>
                                  </>
                                )}
                              </div>
                            </div>
                            <Button
                              size="sm"
                              className="flex-shrink-0 h-8 sm:h-9 bg-violet-600 hover:bg-violet-700"
                              disabled={!fm.completion_path}
                              onClick={(e) => { e.stopPropagation(); fm.completion_path && navigate(fm.completion_path); }}
                            >
                              <span className="hidden sm:inline mr-1">
                                {isInProgress ? 'Continue' : 'Start'}
                              </span>
                              <ArrowRight className="w-4 h-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          )}

          {/* All caught up message — only when nothing is outstanding
              AND we're not actively filtering to a non-matching bucket. */}
          {totalPending === 0 && !statusFilter && (
            <Card className="border-2 border-dashed border-emerald-200 bg-emerald-50/50">
              <CardContent className="py-8 sm:py-12 text-center">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="font-bold text-lg text-emerald-900 mb-1">All caught up!</h3>
                <p className="text-emerald-700 text-sm">
                  You have no pending documents, {(uiLabels.credentials || 'credentials').toLowerCase()}, or forms.
                </p>
              </CardContent>
            </Card>
          )}

          {/* Empty-filter hint — e.g. the user clicked "In Progress"
              but nothing is in progress. */}
          {statusFilter && statusFilter !== 'complete' && filteredPendingDocs.length === 0 && filteredPendingCreds.length === 0 && filteredPendingForms.length === 0 && (
            <Card className="border-2 border-dashed border-slate-200 bg-slate-50/50">
              <CardContent className="py-8 text-center">
                <p className="text-sm text-slate-600">
                  Nothing in this category right now.
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStatusFilter(null)}
                  className="mt-2 text-slate-600"
                >
                  Show all
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Completed section — combines completed docs, credentials,
              and forms. Hidden when the user is filtering to a
              non-complete bucket. */}
          {showCompleted && totalCompleted > 0 && (
            <div ref={completedRef} data-testid="completed-section">
              <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-3 px-1">
                Completed ({totalCompleted})
              </h3>
              <div className="space-y-4">
                {/* Completed Documents */}
                <div className="space-y-2">
                  {completedCount > 0 && (
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 px-1">
                      Documents ({completedCount})
                    </p>
                  )}
                  {(data?.completed_documents || []).sort((a, b) => {
                    const ta = new Date(a.completed_at || a.sent_at || 0).getTime();
                    const tb = new Date(b.completed_at || b.sent_at || 0).getTime();
                    return tb - ta;
                  }).map((doc) => {
                    const status = STATUS_CONFIG[doc.status] || STATUS_CONFIG.completed;
                    const StatusIcon = status.icon;
                    return (
                      <Card
                        key={doc.id}
                        className="bg-slate-50/50 border-slate-200"
                        data-testid={`completed-doc-${doc.id}`}
                      >
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg ${status.bg} flex items-center justify-center flex-shrink-0`}>
                              <StatusIcon className={`w-4 h-4 sm:w-5 sm:h-5 ${status.color}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-medium text-sm break-words" data-testid={`completed-doc-name-${doc.id}`}>{doc.template_name}</h4>
                              <p className="text-xs text-muted-foreground">
                                {status.label} · {doc.completed_at ? format(new Date(doc.completed_at), 'MMM d, yyyy') : 'N/A'}
                              </p>
                            </div>
                            {doc.completed_document_url && (
                              <a
                                href={`${API_URL}${doc.completed_document_url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-violet-600 hover:underline font-medium flex-shrink-0"
                                data-testid={`completed-doc-view-${doc.id}`}
                              >
                                View ↗
                              </a>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>

                {/* Completed Credentials */}
                <div className="space-y-2">
                  {completedCredentialCount > 0 && (
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 px-1">
                      {uiLabels.credentials} ({completedCredentialCount})
                    </p>
                  )}
                  {(data?.completed_credentials || []).sort((a, b) => {
                    const ta = new Date(a.sent_at || a.created_at || 0).getTime();
                    const tb = new Date(b.sent_at || b.created_at || 0).getTime();
                    return tb - ta;
                  }).map((cr) => (
                    <Card
                      key={cr.id}
                      className="bg-slate-50/50 border-slate-200"
                      data-testid={`completed-cred-${cr.id}`}
                    >
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm truncate">{cr.name}</h4>
                            <p className="text-xs text-muted-foreground">
                              Completed · {cr.items_completed} of {cr.items_total} uploaded
                            </p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Completed Forms */}
                <div className="space-y-2">
                  {completedFormCount > 0 && (
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 px-1">
                      Forms ({completedFormCount})
                    </p>
                  )}
                  {(data?.completed_forms || []).sort((a, b) => {
                    const ta = new Date(a.sent_at || a.created_at || 0).getTime();
                    const tb = new Date(b.sent_at || b.created_at || 0).getTime();
                    return tb - ta;
                  }).map((fm) => (
                    <Card
                      key={fm.id}
                      className="bg-slate-50/50 border-slate-200"
                      data-testid={`completed-form-${fm.id}`}
                    >
                      <CardContent className="p-3 sm:p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-sm truncate">{fm.name}</h4>
                            <p className="text-xs text-muted-foreground">Submitted</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Refresh button */}
          <div className="text-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => (portalToken ? loadDocumentsByToken(portalToken) : loadDocuments(data.email))}
              disabled={loading}
              className="text-muted-foreground"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t py-4 px-4">
        <div className="max-w-4xl mx-auto text-center space-y-2">
          {(companySettings.support_email || companySettings.support_phone) && (
            <div className="flex flex-wrap items-center justify-center gap-3 text-xs sm:text-sm text-muted-foreground">
              {companySettings.support_email && (
                <a href={`mailto:${companySettings.support_email}`} className="flex items-center gap-1 hover:text-primary">
                  <Mail className="w-3.5 h-3.5" />
                  {companySettings.support_email}
                </a>
              )}
              {companySettings.support_phone && (
                <a href={`tel:${companySettings.support_phone}`} className="flex items-center gap-1 hover:text-primary">
                  <Phone className="w-3.5 h-3.5" />
                  {companySettings.support_phone}
                </a>
              )}
            </div>
          )}
          <p className="text-xs text-muted-foreground/70">
            Powered by HyreDocs · Secure document handling
          </p>
        </div>
      </footer>
    </div>
  );
}
